import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'
import { Link } from 'react-router-dom'
import {
  AwardGlyph,
  BarChartGlyph,
  BookGlyph,
  BriefcaseGlyph,
  BuildingGlyph,
  CalculatorGlyph,
  CalendarGlyph,
  ChatGlyph,
  ChecklistGlyph,
  ChevronDownGlyph,
  ClipboardCheckGlyph,
  ClockGlyph,
  DashboardGlyph,
  DatabaseGlyph,
  DepartmentsBoardGlyph,
  DocumentGlyph,
  EnvelopeCheckGlyph,
  EnvelopeGlyph,
  FunnelGlyph,
  GaugeGlyph,
  GearGlyph,
  GraduationCapGlyph,
  HierarchyGlyph,
  HourglassGlyph,
  IdCardGlyph,
  MicGlyph,
  MoneyGlyph,
  NetworkGlyph,
  PercentGlyph,
  PieChartGlyph,
  SparkleGlyph,
  StopwatchGlyph,
  UserCheckGlyph,
  UserSearchGlyph,
  UsersGlyph,
  UserXGlyph,
  WalletGlyph,
} from './icons/sidebarIcons'
import { baseLayout } from '../../constants/theme'

/**
 * Normalises the family: fills off, stroke follows the text colour, and one
 * weight across the board. The weight override matters because the glyphs reused
 * straight from `assets/svgs` ship at Lucide's stroke-width 2 (and `chat.svg` /
 * `calendar.svg` at the SVG default of 1) — without this they'd sit at three
 * different weights next to each other.
 */
export const SidebarMenuIcon = styled('span')(() => ({
  marginRight: `calc(${baseLayout.menuItemSpacing} / 2 + 0.7px)`,
  flexShrink: 0,
  fill: 'none',
  '& *': {
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  },
}))

export const DashboardIcon = SidebarMenuIcon.withComponent(DashboardGlyph)
export const BuildingIcon = SidebarMenuIcon.withComponent(BuildingGlyph)
export const DepartmentsBoardIcon = SidebarMenuIcon.withComponent(
  DepartmentsBoardGlyph,
)
export const StarIcon = SidebarMenuIcon.withComponent(SparkleGlyph)
export const FileIcon = SidebarMenuIcon.withComponent(DocumentGlyph)
export const FormIcon = SidebarMenuIcon.withComponent(ChecklistGlyph)
export const AssessmentIcon = SidebarMenuIcon.withComponent(ClipboardCheckGlyph)
export const TriangleIcon = SidebarMenuIcon.withComponent(BriefcaseGlyph)
export const PersonIcon = SidebarMenuIcon.withComponent(UsersGlyph)
export const ChatIcon = SidebarMenuIcon.withComponent(ChatGlyph)
export const OfferIcon = SidebarMenuIcon.withComponent(EnvelopeGlyph)
export const ChartIcon = SidebarMenuIcon.withComponent(BarChartGlyph)
export const CalendarIcon = SidebarMenuIcon.withComponent(CalendarGlyph)
export const GearIcon = SidebarMenuIcon.withComponent(GearGlyph)
export const DatabaseIcon = SidebarMenuIcon.withComponent(DatabaseGlyph)
export const HierarchyIcon = SidebarMenuIcon.withComponent(HierarchyGlyph)
export const OrgChartIcon = SidebarMenuIcon.withComponent(NetworkGlyph)
export const EmployeeIcon = SidebarMenuIcon.withComponent(IdCardGlyph)
export const TrainingHubIcon = SidebarMenuIcon.withComponent(GraduationCapGlyph)
export const MyLearningIcon = SidebarMenuIcon.withComponent(BookGlyph)
export const FunnelIcon = SidebarMenuIcon.withComponent(FunnelGlyph)
export const ClockIcon = SidebarMenuIcon.withComponent(ClockGlyph)
export const AwardIcon = SidebarMenuIcon.withComponent(AwardGlyph)
export const GaugeIcon = SidebarMenuIcon.withComponent(GaugeGlyph)
export const MicIcon = SidebarMenuIcon.withComponent(MicGlyph)
export const PercentIcon = SidebarMenuIcon.withComponent(PercentGlyph)
export const MoneyIcon = SidebarMenuIcon.withComponent(MoneyGlyph)
export const HourglassIcon = SidebarMenuIcon.withComponent(HourglassGlyph)
export const StopwatchIcon = SidebarMenuIcon.withComponent(StopwatchGlyph)
export const UserCheckIcon = SidebarMenuIcon.withComponent(UserCheckGlyph)
export const UserSearchIcon = SidebarMenuIcon.withComponent(UserSearchGlyph)
export const PieChartIcon = SidebarMenuIcon.withComponent(PieChartGlyph)
export const EnvelopeCheckIcon =
  SidebarMenuIcon.withComponent(EnvelopeCheckGlyph)
export const UserXIcon = SidebarMenuIcon.withComponent(UserXGlyph)
export const CalculatorIcon = SidebarMenuIcon.withComponent(CalculatorGlyph)
export const WalletIcon = SidebarMenuIcon.withComponent(WalletGlyph)

export const VerticalMenu = styled('div')(
  ({ collapsed, drawerOpen, locale, theme }) => ({
    width: collapsed
      ? `${baseLayout.sidebarCollapsedWidth} !important`
      : baseLayout.sidebarWidth,
    zIndex: '1001',
    background: theme.ateneoBlue,
    bottom: '0',
    marginTop: '0',
    position: 'fixed',
    top: baseLayout.headerHeight,
    paddingTop: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '2px 0 16px rgba(0, 0, 0, 0.08)',
    transition: 'width 0.3s ease-in-out',
    // On phones the sidebar becomes an off-canvas drawer instead of a rail
    [`@media (max-width: ${baseLayout.mobileBreakpoint}px)`]: {
      top: baseLayout.mobileHeaderHeight,
      width: `${baseLayout.sidebarWidth} !important`,
      maxWidth: '80vw',
      transform: drawerOpen
        ? 'translateX(0)'
        : `translateX(${locale === 'ar' ? '100%' : '-100%'})`,
      visibility: drawerOpen ? 'visible' : 'hidden',
      transition: 'transform 0.3s ease-in-out, visibility 0.3s ease-in-out',
    },
    '@media print': {
      display: 'none !important',
    },
  }),
)

export const DrawerOverlay = styled('div')(({ open }) => ({
  display: 'none',
  [`@media (max-width: ${baseLayout.mobileBreakpoint}px)`]: {
    display: 'block',
    position: 'fixed',
    top: baseLayout.mobileHeaderHeight,
    right: 0,
    bottom: 0,
    left: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.45)',
    zIndex: 1000,
    opacity: open ? 1 : 0,
    pointerEvents: open ? 'auto' : 'none',
    transition: 'opacity 0.3s ease-in-out',
  },
  '@media print': {
    display: 'none !important',
  },
}))

export const MenuItemBadge = styled('span')(
  ({ fontSize = '11px', top, right, theme }) => ({
    color: theme.sidebarTextColor,
    minWidth: '20px',
    height: '20px',
    padding: '0 6px',
    fontSize,
    backgroundColor: theme.isLightCustomColor
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.16)',
    borderRadius: '6px',
    fontWeight: 600,
    lineHeight: 1,
    display: 'inline-flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 2,
    transition: 'all 0.2s ease',

    ...(top &&
      right && {
        position: 'absolute',
        top,
        right,
        transform: 'translateY(-50%)',
      }),
  }),
)

const fadeIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`

export const StyledSidebar = styled.aside(({ theme }) => ({
  color: theme.sidebarTextColor,
  position: 'relative',
  width: '100%',
  alignItems: 'center',
}))

export const StyledTopSpacing = styled.div({
  height: '16px',
})

export const SectionTitle = styled.div(({ theme }) => ({
  padding: '0 14px',
  fontSize: '10.5px',
  color: theme.sidebarSectionTitleColor,
  marginBottom: '10px',
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  fontWeight: 600,
  opacity: 0.85,
  width: '100%',
}))

const sidebarHoverTint = (theme) =>
  theme.isLightCustomColor ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.07)'
const sidebarActiveTint = (theme) =>
  theme.isLightCustomColor ? 'rgba(0, 0, 0, 0.09)' : 'rgba(255, 255, 255, 0.14)'
const sidebarActiveBorder = (theme) =>
  theme.isLightCustomColor ? 'rgba(0, 0, 0, 0.16)' : 'rgba(255, 255, 255, 0.28)'

export const MenuButton = styled('button', {
  shouldForwardProp: (prop) =>
    prop !== 'active' && prop !== 'isExpanded' && prop !== 'as',
})(({ active, isExpanded, theme }) => ({
  display: 'flex',
  background: active ? sidebarActiveTint(theme) : 'transparent',
  borderLeft: active ? `3px solid ${theme.water}` : '1px solid transparent',
  borderTop: 'none',
  borderBottom: 'none',
  borderRight: 'none',
  alignItems: 'center',
  padding: isExpanded ? '9px 12px' : '9px 0 9px 14px',
  margin: '2px auto',
  borderRadius: '10px',
  transition: 'background-color 200ms ease, color 200ms ease',
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  color: theme.sidebarTextColor,
  textDecoration: 'none',
  '&:hover': {
    backgroundColor: active
      ? sidebarActiveTint(theme)
      : sidebarHoverTint(theme),
  },
  // Selected row: full-strength icon, so the state reads in the glyph too.
  ...(active && {
    '& > div > svg': { opacity: 1 },
  }),
  ...(isExpanded
    ? {
        justifyContent: 'space-between',
        width: 'calc(100% - 16px)',
      }
    : {
        justifyContent: 'center',
        width: '44px',
      }),
  ':dir(rtl)': {
    padding: isExpanded ? '9px 12px' : '9px 14px 9px 0',
  },
}))

export const IconContainer = styled.div(({ isExpanded }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '11px',
  justifyContent: 'center',
  position: 'relative',
  '& svg': {
    width: '18px',
    height: '18px',
    flexShrink: 0,
    margin: 0,
    opacity: 0.8,
    transition: 'opacity 200ms ease',
  },
  'button:hover > & > svg, a:hover > & > svg': {
    opacity: 1,
  },
  '& span': {
    opacity: isExpanded ? 1 : 0,
    maxWidth: isExpanded ? '100%' : 0,
    transition: 'opacity 0.2s ease 0.1s, max-width 0.3s ease-in-out',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
  },
}))

export const DropdownIcon = styled(ChevronDownGlyph, {
  shouldForwardProp: (prop) => prop !== '$isOpen',
})(({ $isOpen }) => ({
  flexShrink: 0,
  opacity: $isOpen ? 1 : 0.7,
  transition:
    'transform 220ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease',
  transform: `rotate(${$isOpen ? '180deg' : '0'})`,
}))

export const Tooltip = styled.div(({ dir, theme }) => ({
  position: 'fixed',
  backgroundColor: theme.ateneoBlue,
  color: theme.sidebarTextColor,
  borderRadius: '6px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  minWidth: '160px',
  padding: '4px 0',
  animation: `${fadeIn} 200ms ease-out`,
  border: `1px solid ${theme.sidebar}`,
  zIndex: 1002,
  ...(dir === 'rtl' && {
    right: 'auto',
    left: 'auto',
    transformOrigin: 'right',
    '&:before': {
      right: '-8px',
      left: 'auto',
      borderRightColor: theme.sidebar,
    },
  }),
}))

export const NestedTooltip = styled.div(({ theme }) => ({
  position: 'absolute',
  left: '100%',
  top: 0,
  marginLeft: '4px',
  backgroundColor: theme.ateneoBlue,
  borderRadius: '6px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.1)',
  minWidth: '160px',
  padding: '4px 0',
  transformOrigin: 'left',
  animation: `${fadeIn} 200ms ease-out`,
  border: `1px solid ${theme.sidebar}`,
  zIndex: 1002,

  ':dir(rtl)': {
    left: 'auto',
    right: '100%',
    marginLeft: 0,
    marginRight: '4px',
    transformOrigin: 'right',
  },
}))

export const TooltipItem = styled.div(({ theme }) => ({
  padding: '6px 12px',
  fontSize: '12px',
  fontWeight: 600,
  borderBottom: `1px solid ${theme.sidebar}`,
  ':dir(rtl)': {
    textAlign: 'right',
  },
  width: '100%',
}))

export const SubItemLink = styled(Link, {
  shouldForwardProp: (prop) => prop !== 'active',
})(({ active, theme }) => {
  const mutedText = theme.isLightCustomColor
    ? 'rgba(0, 0, 0, 0.55)'
    : 'rgba(255, 255, 255, 0.6)'
  const hoverText = theme.isLightCustomColor
    ? 'rgba(0, 0, 0, 0.85)'
    : 'rgba(255, 255, 255, 0.9)'
  const dotColor = active
    ? theme.sidebarTextColor
    : theme.isLightCustomColor
      ? 'rgba(0, 0, 0, 0.35)'
      : 'rgba(255, 255, 255, 0.4)'

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '7px 12px',
    fontSize: '12.5px',
    fontWeight: active ? 600 : 500,
    borderRadius: '8px',
    transition: 'background-color 200ms ease, color 200ms ease',
    textDecoration: 'none',
    color: active ? theme.sidebarTextColor : mutedText,
    backgroundColor: active ? sidebarActiveTint(theme) : 'transparent',
    '&::before': {
      content: '""',
      flexShrink: 0,
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      backgroundColor: dotColor,
      transition: 'background-color 200ms ease',
    },
    '&:hover': {
      backgroundColor: active
        ? sidebarActiveTint(theme)
        : sidebarHoverTint(theme),
      color: active ? theme.sidebarTextColor : hoverText,
    },
    ':dir(rtl)': {
      textAlign: 'right',
    },
  }
})

export const DropdownGroupButton = styled.button(({ active, theme }) => {
  const mutedText = theme.isLightCustomColor
    ? 'rgba(0, 0, 0, 0.55)'
    : 'rgba(255, 255, 255, 0.6)'
  const hoverText = theme.isLightCustomColor
    ? 'rgba(0, 0, 0, 0.85)'
    : 'rgba(255, 255, 255, 0.9)'

  return {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
    width: '100%',
    padding: '7px 12px',
    fontSize: '12.5px',
    fontWeight: active ? 600 : 500,
    textAlign: 'start',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'background-color 200ms ease, color 200ms ease',
    color: active ? theme.sidebarTextColor : mutedText,
    backgroundColor: active ? sidebarActiveTint(theme) : 'transparent',
    '&:hover': {
      backgroundColor: active
        ? sidebarActiveTint(theme)
        : sidebarHoverTint(theme),
      color: active ? theme.sidebarTextColor : hoverText,
    },
    '&:focus-visible': {
      outline: `2px solid ${sidebarActiveBorder(theme)}`,
      outlineOffset: '1px',
    },
    ':dir(rtl) &': {
      textAlign: 'right',
    },
  }
})

export const DropdownMenu = styled.div(({ theme }) => ({
  backgroundColor: theme.isLightCustomColor
    ? 'rgba(0, 0, 0, 0.04)'
    : 'rgba(255, 255, 255, 0.05)',
  margin: '4px auto 0',
  borderRadius: '10px',
  padding: '4px',
  width: 'calc(100% - 16px)',
}))

export const SectionContainer = styled.div({
  width: '100%',
  padding: '16px 0',
  display: 'flex',
  flexDirection: 'column',
})

export const SubItemsContainer = styled.div({
  padding: '4px 0',
  width: '100%',
  ':dir(rtl) &': {
    direction: 'rtl',
    textAlign: 'right',
  },
})

export const MenuItemWrapper = styled.div({
  position: 'relative',
})

export const DropdownSubItemLink = styled(SubItemLink)(({ theme }) => ({
  padding: '7px 12px',
  borderRadius: '8px',
  '&:hover': {
    backgroundColor: sidebarHoverTint(theme),
  },
  ':dir(rtl)': {
    textAlign: 'right',
    paddingRight: '12px',
  },
}))

export const ScrollList = styled('ul')({
  display: 'grid',
  gridAutoFlow: 'row',
  alignContent: 'start',
  overflowY: 'auto',
  overscrollBehaviorInline: 'contain',
  padding: '0 !important',
  margin: 0,
  flex: '1 1 auto',
  minHeight: 0,
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    borderRadius: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: 'transparent',
  },
})

export const SidebarUserCard = styled.div(({ isExpanded, theme }) => ({
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  margin: isExpanded ? '8px 12px 14px' : '8px auto 14px',
  padding: isExpanded ? '10px 12px' : '8px',
  borderRadius: '12px',
  backgroundColor: theme.isLightCustomColor
    ? 'rgba(0, 0, 0, 0.05)'
    : 'rgba(255, 255, 255, 0.06)',
  border: `1px solid ${
    theme.isLightCustomColor
      ? 'rgba(0, 0, 0, 0.06)'
      : 'rgba(255, 255, 255, 0.08)'
  }`,
  width: isExpanded ? 'calc(100% - 24px)' : '48px',
  justifyContent: isExpanded ? 'flex-start' : 'center',
  overflow: 'hidden',
}))

export const SidebarUserInfo = styled.div({
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  lineHeight: 1.25,
})

export const SidebarUserName = styled.span(({ theme }) => ({
  fontSize: '13px',
  fontWeight: 600,
  color: theme.sidebarTextColor,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

export const SidebarUserRole = styled.span(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 500,
  color: theme.sidebarSectionTitleColor,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))
